import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Plus, Edit, Trash2, Search, Inbox, MessageSquare, Tag } from "lucide-react";
import { toast } from "sonner";

const HORSE_PLACEHOLDER = "https://images.pexels.com/photos/32076745/pexels-photo-32076745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const AdminDashboard = () => {
  const [horses, setHorses] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/horses", { params: { q: query || undefined, limit: 100 } });
      setHorses(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/submissions", { params: { status: "pending" } }).then(r => setPendingCount(r.data?.length || 0)).catch(() => {});
    api.get("/admin/comments").then(r => setCommentsCount(r.data?.length || 0)).catch(() => {});
  }, [load]);

  const onDelete = async (horse) => {
    if (!window.confirm(`"${horse.name}" адууг устгах уу?`)) return;
    try {
      await api.delete(`/horses/${horse.id}`);
      toast.success("Амжилттай устгагдлаа");
      load(q);
    } catch { toast.error("Устгах үед алдаа гарлаа"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#B8860B] mb-2 font-bold">Удирдлагын самбар</div>
          <h1 className="text-3xl font-bold text-[#1B3B5A]">Адуудын жагсаалт</h1>
        </div>
        <Link to="/admin/new" className="inline-flex items-center gap-2 bg-[#1B3B5A] text-white hover:bg-[#264F78] px-4 py-2.5 rounded font-bold transition-colors text-sm" data-testid="add-horse-btn">
          <Plus size={16} strokeWidth={1.5} /> Шинээр бүртгэх
        </Link>
      </div>

      {/* Admin Shortcuts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Link to="/admin/submissions" className="bg-white border border-[#D6CFC4] hover:border-[#B8860B] rounded p-4 flex items-center gap-3 transition-colors" data-testid="admin-submissions-link">
          <div className="p-2 bg-[#B8860B]/10 text-[#B8860B] rounded"><Inbox size={20} /></div>
          <div>
            <div className="text-sm font-bold text-[#1B3B5A]">Ирсэн хүсэлтүүд</div>
            <div className="text-xs text-[#5C4F42]">{pendingCount} шинэ</div>
          </div>
        </Link>
        <Link to="/admin/comments" className="bg-white border border-[#D6CFC4] hover:border-[#B8860B] rounded p-4 flex items-center gap-3 transition-colors" data-testid="admin-comments-link">
          <div className="p-2 bg-[#1B3B5A]/10 text-[#1B3B5A] rounded"><MessageSquare size={20} /></div>
          <div>
            <div className="text-sm font-bold text-[#1B3B5A]">Сэтгэгдлүүд</div>
            <div className="text-xs text-[#5C4F42]">{commentsCount} нийт</div>
          </div>
        </Link>
        <div className="bg-white border border-[#D6CFC4] rounded p-4 flex items-center gap-3">
          <div className="p-2 bg-[#8B2500]/10 text-[#8B2500] rounded"><Tag size={20} /></div>
          <div>
            <div className="text-sm font-bold text-[#1B3B5A]">Зарагдах адуу</div>
            <div className="text-xs text-[#5C4F42]">{horses.filter(h => h.is_for_sale).length} идэвхтэй</div>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="mb-6 flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C4F42]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Адуу хайх..."
            className="w-full border border-[#D6CFC4] rounded pl-9 pr-3 py-2 focus:outline-none focus:border-[#B8860B]"
            data-testid="admin-search-input"
          />
        </div>
        <button type="submit" className="bg-[#B8860B] text-white hover:bg-[#996F09] px-4 py-2 rounded">Хайх</button>
      </form>

      <div className="bg-white border border-[#D6CFC4] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5C4F42]">Ачааллаж байна...</div>
        ) : horses.length === 0 ? (
          <div className="p-10 text-center text-[#5C4F42]">Адуу бүртгэгдээгүй байна.</div>
        ) : (
          <div className="divide-y divide-[#E5DFD3]">
            {horses.map((h) => (
              <div key={h.id} className="flex items-center gap-4 p-4 hover:bg-[#F0EBE1]/30 transition-colors" data-testid={`admin-row-${h.id}`}>
                <img src={h.photo_path ? fileUrl(h.photo_path) : HORSE_PLACEHOLDER} alt={h.name} className="w-12 h-12 rounded object-cover border border-[#D6CFC4]" />
                <div className="flex-1 min-w-0">
                  <Link to={`/horse/${h.id}`} className="text-base font-bold text-[#1B3B5A] hover:text-[#B8860B] transition-colors">
                    {h.name}
                  </Link>
                  <div className="text-xs text-[#5C4F42] mt-0.5 truncate">
                    {[h.registration_number && `№${h.registration_number}`, h.microchip && `чип:${h.microchip}`, h.color, h.birth_year && `${h.birth_year} он`, h.country].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {h.is_for_sale && <span className="text-[10px] bg-[#B8860B] text-white px-2 py-0.5 rounded">ЗАР</span>}
                <Link to={`/admin/edit/${h.id}`} className="p-2 text-[#1B3B5A] hover:bg-[#1B3B5A]/10 rounded transition-colors" data-testid={`admin-edit-${h.id}`}>
                  <Edit size={16} strokeWidth={1.5} />
                </Link>
                <button onClick={() => onDelete(h)} className="p-2 text-[#8B2500] hover:bg-[#8B2500]/10 rounded transition-colors" data-testid={`admin-delete-${h.id}`}>
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
